import { useEffect, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  DirectionEnum,
  dropdownPassengersWithStationsAtom,
  dropdownStationListAtom,
  filterHomeStationsCategory,
  filterWorkStationsCategory,
  IDropdownItemStations,
  IPassengerForStationDto,
  stationsForHomeSelector,
  stationsForWorkSelector,
} from "@ryde/shared";

import { useRideStationActions } from "../rides";

/**
 * Both of these are shared rather than written inline, because a fresh `[]` is a new identity every
 * render: the default parameter feeds the `selectedPassengers` memo, and that memo's empty case feeds
 * both station memos, so an inline literal in either place re-runs a full scan of every active station.
 * Fixing only one of the two leaves the other still minting arrays, so both are needed.
 *
 * This is not hypothetical: the caller passes `undefined` until a passenger is picked, which is exactly
 * while the station dropdowns are already on screen.
 */
const EMPTY_PASSENGER_IDS: number[] = [];
const EMPTY_PASSENGERS: IPassengerForStationDto[] = [];

export interface IUseStationsOptions {
  direction: DirectionEnum;
  passengersWatchedPassengerIds?: number[];
}

export const useStationsOptions = ({
  direction,
  passengersWatchedPassengerIds = EMPTY_PASSENGER_IDS,
}: IUseStationsOptions) => {
  const stationsForHomeCategories = useRecoilValue(stationsForHomeSelector);
  const stationsForWorkCategories = useRecoilValue(stationsForWorkSelector);
  const stationsDropdownList = useRecoilValue(dropdownStationListAtom);
  const passengersDropdownList = useRecoilValue(
    dropdownPassengersWithStationsAtom,
  );
  const { fetchDropdownPassengersAndStations } = useRideStationActions();
  const isPickUp = direction === DirectionEnum.PickUp;

  const isDataLoading = passengersDropdownList === undefined;

  const selectedPassengers = useMemo(() => {
    if (!passengersWatchedPassengerIds.length) {
      return EMPTY_PASSENGERS;
    }

    const passengersWatchedPassengerIdsSet = new Set(
      passengersWatchedPassengerIds,
    );
    return passengersDropdownList?.filter(x =>
      passengersWatchedPassengerIdsSet.has(x.id),
    );
  }, [passengersDropdownList, passengersWatchedPassengerIds]);

  const stationsDropdownListFiltered: IDropdownItemStations[] = useMemo(() => {
    const activeStations = stationsDropdownList?.filter(s => s?.isActive);
    return activeStations || [];
  }, [stationsDropdownList]);

  const stationsFromDropdownList = useMemo(() => {
    if (isPickUp) {
      return filterHomeStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: stationsDropdownListFiltered,
      });
    } else {
      return filterWorkStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: stationsDropdownListFiltered,
      });
    }
  }, [
    isPickUp,
    selectedPassengers,
    stationsForHomeCategories,
    stationsForWorkCategories,
    stationsDropdownListFiltered,
  ]);

  const stationsToDropdownList = useMemo(() => {
    if (isPickUp) {
      return filterWorkStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForWorkCategories,
        stationsDropdownList: stationsDropdownListFiltered,
      });
    } else {
      return filterHomeStationsCategory({
        passengers: selectedPassengers,
        stationCategories: stationsForHomeCategories,
        stationsDropdownList: stationsDropdownListFiltered,
      });
    }
  }, [
    isPickUp,
    selectedPassengers,
    stationsForWorkCategories,
    stationsForHomeCategories,
    stationsDropdownListFiltered,
  ]);

  useEffect(() => {
    if (passengersDropdownList !== undefined) {
      return;
    }

    void fetchDropdownPassengersAndStations();
  }, [fetchDropdownPassengersAndStations, passengersDropdownList]);

  return {
    stationsFromDropdownList,
    stationsToDropdownList,
    isDataLoading,
  };
};
